import { lazy, Suspense } from "react";
import { useMediaQuery } from "react-responsive";
import { Navigate, Route, Routes } from "react-router-dom";

import ChangeLogs from "./change-logs";
import { AppProvider } from "./components/AppProvider.jsx";
import BussinessLayout from "./components/BussinessLayout.jsx";
import Dashboard from "./components/Dashboard.jsx";
import GatewayForm from "./components/forms/GatewayForm.jsx";
import TicketForm from "./components/forms/TicketForm.jsx";
import BussinessDashboard from "./components/main/bussiness/BussinessDashboard.jsx";
import BussinessDetail from "./components/main/bussiness/BussinessDetail.jsx";
import BussinessList from "./components/main/bussiness/BussinessList.jsx";
import Exports from "./components/main/export/Exports.jsx";
import FormPage from "./components/main/Form/FormPage.jsx";
import NewPaymentForm from "./components/main/Form/NewPaymentForm.jsx";
import PaymentLinkList from "./components/main/Form/PaymentLinkList.jsx";
import QRCode from "./components/main/Form/QRCode.jsx";
import Transactios from "./components/main/Form/Transactios.jsx";
import MaketerSettlements from "./components/main/marketing/MaketerSettlements.jsx";
import MarketerRefferedUsers from "./components/main/marketing/MarketerRefferedUsers.jsx";
import MarketingLayout from "./components/main/marketing/MarketingLayout.jsx";
import MessagesPage from "./components/main/messages/MessagesPage.jsx";
import NotifPage from "./components/main/messages/NotifPage.jsx";
import NewBusiness from "./components/main/newbussiness/NewBusiness.jsx";
import NotificationsMobilePage from "./components/main/notifications/NotificationsMobilePage.jsx";
import GatewayList from "./components/main/port/GatewayList.jsx";
import PosTerminalList from "./components/main/pos/PosTerminalList.jsx";
import PosTransactionsList from "./components/main/pos/PosTransactionsList.jsx";
import SettingDashboard from "./components/main/SettingCategory/SettingDashboard.jsx";
import NewSettlment from "./components/main/settlements/NewSettlment.jsx";
import SettlementList from "./components/main/settlements/Settlements.jsx";
import ChatPage from "./components/main/tickets/ChatPage.jsx";
import MobileChatPage from "./components/main/tickets/MobileChatPage.jsx";
import Tickets from "./components/main/tickets/Tickets.jsx";
import NewTransactionsList from "./components/main/transaction/NewTransactionsList.jsx";
import TransactionsInfo from "./components/main/transaction/TransactionsInfo.jsx";
import NotFound from "./components/NotFound.jsx";
import ProfileNoticeSetting from "./components/profile/ProfileNoticeSetting.jsx";
import HealthCheck from "./components/shared/HealthCheck.jsx";
import AuthLayoutLoader from "./features/authentication/components/loader.js";
import { AuthProvider } from "./features/authentication/provider.js";
import { bankAccountRoutes } from "./features/bank-accounts/routes";
import UserValidationProvider from "./features/users/components/user-validation-provider";
import UserLayout from "./features/users/layouts/user-layout";
import Profile from "./features/users/profile";
import Status from "./features/users/status";

const SignInPage = lazy(() => import("features/authentication/signin"));
const VerificationPage = lazy(() => import("features/authentication/verification"));

const App = () => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  return (
    <Routes>
      <Route element={<AuthProvider />}>
        <Route path="/" element={<Navigate to="/user" />} />
        <Route
          path="/auth"
          element={
            <Suspense fallback={<AuthLayoutLoader />}>
              <SignInPage />
            </Suspense>
          }
        />
        <Route
          path="/verification"
          element={
            <Suspense fallback={<AuthLayoutLoader />}>
              <VerificationPage />
            </Suspense>
          }
        />
      </Route>
      <Route path="/f/:id" element={<NewPaymentForm />} />
      {isMobile && <Route path="ticketChat/:id" element={<MobileChatPage />} />}
      <Route path="/form/transaction/:tracking_code" element={<Transactios />} />
      <Route path="/form/QRcode/:id" element={<QRCode />} />

      <Route element={<AppProvider />}>
        <Route element={<Dashboard />}>
          <Route element={<UserLayout />}>
            <Route path="user" element={<Profile />} />
            <Route path="profile" element={<Status />} />
            {bankAccountRoutes.map((route) => (
              <Route key={route.path} path={route.path} element={route.element} />
            ))}
            <Route path="tickets/list" element={<Tickets />} />
            <Route path="tickets/new" element={<TicketForm />} />
            <Route path="ticketChat/:id" element={<ChatPage />} />

            <Route
              path="profile-notification-setting"
              element={
                <UserValidationProvider>
                  <ProfileNoticeSetting />
                </UserValidationProvider>
              }
            />
            <Route
              path="notices"
              element={
                <UserValidationProvider>
                  <NotificationsMobilePage />
                </UserValidationProvider>
              }
            />
            <Route path="messages/notif" element={<NotifPage />} />
            <Route path="messages/inbox" element={<MessagesPage />} />
            <Route path="marketing" element={<MarketingLayout />}>
              <Route path="users" element={<MarketerRefferedUsers />} />
              <Route path="settlements" element={<MaketerSettlements />} />
            </Route>
            <Route path="setting" element={<SettingDashboard />} />
            <Route path="business/new" element={<NewBusiness />} />
            <Route
              path="business/list"
              element={
                <UserValidationProvider>
                  <BussinessList />
                </UserValidationProvider>
              }
            />
          </Route>
          <Route element={<BussinessLayout />}>
            <Route path="business/:type/:id" element={<BussinessDashboard />} />
            <Route path="business/detail" element={<BussinessDetail />} />
            <Route path="gateway/list" element={<GatewayList />} />
            <Route path="gateway/new" element={<GatewayForm />} />
            {/* @ts-expect-error form page props */}
            <Route path="forms/:id" element={<FormPage />} />
            <Route path="forms/list" element={<PaymentLinkList />} />
            <Route path="transactions" element={<NewTransactionsList />} />
            <Route path="transaction/info/:id" element={<TransactionsInfo />} />
            <Route path="settlement/list" element={<SettlementList />} />
            <Route path="settlement/new" element={<NewSettlment />} />
            <Route path="terminals" element={<PosTerminalList />} />
            <Route path="pos-transactions" element={<PosTransactionsList />} />
            <Route path="exports" element={<Exports />} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<NotFound />} />
      <Route path="hc" element={<HealthCheck />} />
      <Route path="changelogs" element={<ChangeLogs />} />
    </Routes>
  );
};

export default App;
